import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts'
import { fetchUserProfile, updateUserProfile } from '@/lib/api/user-profile'
import type { UserProfileUpdatePayload } from '@/types/user-profile'
import { toast } from 'sonner'

/**
 * Query key factory for user profiles
 * Provides consistent keys for cache management
 */
export const userProfileKeys = {
  all: ['user-profile'] as const,
  details: () => [...userProfileKeys.all, 'detail'] as const,
  detail: (userId: string) => [...userProfileKeys.details(), userId] as const,
}

/**
 * Fetch the current user's profile
 */
export function useUserProfile() {
  const { user } = useAuth()
  const userId = user?.id

  return useQuery({
    queryKey: userProfileKeys.detail(userId || ''),
    queryFn: () => fetchUserProfile(userId!),
    enabled: !!userId,
  })
}

/**
 * Update the current user's profile
 */
export function useUpdateUserProfile() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserProfileUpdatePayload }) =>
      updateUserProfile(id, data),
    onSuccess: () => {
      // Invalidate the profile query to refetch
      const userId = user?.id
      if (userId) {
        queryClient.invalidateQueries({ queryKey: userProfileKeys.detail(userId) })
      }
      toast.success('Profile updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update profile', {
        description: error.message,
      })
    },
  })
}
